import { AxiosResponse } from "axios";

import { API } from "../index";
import type {
  CustomerPhoneCaptureDetail,
  CustomerPhoneCaptureMode,
  TableOrder,
} from "./table-order.service";

const BASE = "/api/v2";

export type FrontTerminalStatus = "ACTIVE" | "DISABLED" | "REVOKED";

export type FrontTerminal = {
  id: number;
  site: number;
  site_name?: string;
  site_slogan?: string;
  serial_number: string;
  merchant_id: string;
  merchant_name: string;
  label: string;
  status: FrontTerminalStatus;
  capabilities?: {
    supports_customer_phone_input: boolean;
    supports_customer_phone_qr: boolean;
  };
  auth_token_hint: string;
  plugin_version: string;
  paired_at: string | null;
  last_seen_at: string | null;
  last_polled_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
};

export type FrontTerminalPairingSession = {
  session_id: number;
  pairing_code: string;
  terminal_label: string;
  expires_at: string;
};

export type FrontTerminalUpdatePayload = {
  label?: string;
  status?: Extract<FrontTerminalStatus, "DISABLED" | "REVOKED">;
};

export type TableOrderPaymentRequestType = "PAY" | "CANCEL" | "REPRINT";
export type TableOrderPaymentStatus = "PENDING" | "PROCESSING" | "SUCCEEDED" | "FAILED";
export type TableOrderPostCancelAction = "REOPEN_PAYMENT" | "CANCEL_ORDER";

export type TableOrderPayment = {
  id: number;
  site: number;
  table_order: number;
  terminal: number;
  terminal_label: string;
  requested_by: number | null;
  parent_payment: number | null;
  request_id: string;
  request_type: TableOrderPaymentRequestType;
  status: TableOrderPaymentStatus;
  amount: number;
  tax_amount: number;
  supply_amount: number;
  tax_free_amount: number;
  tip_amount: number;
  payment_key: string;
  payment_method: string;
  approval_number: string;
  installment_months: number;
  terminal_transaction_id: string;
  error_code: string;
  error_message: string;
  request_payload: Record<string, any>;
  result_payload: Record<string, any>;
  requested_at: string;
  processing_started_at: string | null;
  completed_at: string | null;
  updated_at: string;
};

export type SubmitTableOrderItemOptionPayload = {
  menu_option_id: number;
  quantity?: number;
};

export type SubmitTableOrderItemPayload = {
  menu_id: number;
  quantity?: number;
  options?: SubmitTableOrderItemOptionPayload[];
};

export type SubmitTableOrderPayload = {
  table_id?: number | null;
  memo?: string;
  draft_key?: string;
  order_source?: TableOrder["order_source"];
  order_type: "serving" | "pickup";
  customer_phone_num?: string;
  customer_name?: string;
  customer_phone_capture_mode?: CustomerPhoneCaptureMode;
  customer_phone_capture_detail?: CustomerPhoneCaptureDetail;
  items?: SubmitTableOrderItemPayload[];
};

export type CreateTableOrderPaymentPayload = {
  terminal_id: number;
  amount?: number;
  tax_amount?: number;
  supply_amount?: number;
  tax_free_amount?: number;
  tip_amount?: number;
};

export const posService = {
  issueTerminalPairingSession: (
    payload: { terminal_label?: string; expires_in_seconds?: number } = {}
  ): Promise<AxiosResponse<FrontTerminalPairingSession>> => API.post(`${BASE}/pos/pairings`, payload),

  listFrontTerminals: (): Promise<AxiosResponse<FrontTerminal[]>> => API.get(`${BASE}/pos/terminals`),

  getFrontTerminal: (terminalId: number): Promise<AxiosResponse<FrontTerminal>> =>
    API.get(`${BASE}/pos/terminals/${terminalId}`),

  updateFrontTerminal: (
    terminalId: number,
    payload: FrontTerminalUpdatePayload
  ): Promise<AxiosResponse<FrontTerminal>> => API.patch(`${BASE}/pos/terminals/${terminalId}`, payload),

  submitTableOrder: (payload: SubmitTableOrderPayload): Promise<AxiosResponse<TableOrder>> =>
    API.post(`${BASE}/table-orders/submit/`, payload),

  listTableOrderPayments: (tableOrderId: number): Promise<AxiosResponse<TableOrderPayment[]>> =>
    API.get(`${BASE}/pos/table-orders/${tableOrderId}/payments`),

  createTableOrderPayment: (
    tableOrderId: number,
    payload: CreateTableOrderPaymentPayload
  ): Promise<AxiosResponse<TableOrderPayment>> => API.post(`${BASE}/pos/table-orders/${tableOrderId}/payments`, payload),

  requestPaymentCancel: (
    paymentId: number,
    payload: { reason?: string } = {}
  ): Promise<AxiosResponse<TableOrderPayment>> => API.post(`${BASE}/pos/payments/${paymentId}/cancel`, payload),

  requestPaymentReprint: (
    paymentId: number,
    payload: { reason?: string } = {}
  ): Promise<AxiosResponse<TableOrderPayment>> => API.post(`${BASE}/pos/payments/${paymentId}/reprint`, payload),

  submitTableOrderPostCancelAction: (
    tableOrderId: number,
    payload: { action: TableOrderPostCancelAction }
  ): Promise<AxiosResponse<TableOrder>> => API.post(`${BASE}/pos/table-orders/${tableOrderId}/post-cancel-action`, payload),
};